import { tileArtworkChildren, clearTileArtwork } from './tileArtwork';
import { Color, Sprite, type ColorSource, type Graphics } from 'pixi.js';
import { colors } from '@/styles';

export function multiplyTint(source: ColorSource, tint: number): number {
  const color = Color.shared.setValue(source).toNumber();
  const channel = (shift: number) => Math.round(((color >> shift) & 255) * ((tint >> shift) & 255) / 255);
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}

const background = Color.shared.setValue(colors.tileBackground).toNumber();

/** Keep the tile body neutral while applying the chosen hue to colored details. */
export function recolorTileDetails(graphics: Graphics, tint: number) {
  const seen = new Set<object>();
  const recolor = (source: number) => {
    if (source === background || source === 0x00ff00) return source;
    const r = (source >> 16) & 255, g = (source >> 8) & 255, b = source & 255;
    // Neutral text, borders, and backgrounds retain their contrast.
    if (Math.max(r, g, b) - Math.min(r, g, b) < 16) return source;
    const strength = Math.max(r, g, b) / 255;
    const channel = (shift: number) => Math.round(((tint >> shift) & 255) * strength);
    return (channel(16) << 16) | (channel(8) << 8) | channel(0);
  };
  for (const instruction of graphics.context.instructions) {
    if (instruction.action !== 'fill' && instruction.action !== 'stroke') continue;
    const style = instruction.data.style;
    if (seen.has(style)) continue;
    seen.add(style);
    style.color = recolor(Color.shared.setValue(style.color).toNumber());
  }
  graphics.context.emit('update', graphics.context);
  // Gate, clock and random icons are white SVG sprites tinted by their signal
  // color. Recolor that tint too; pixel-art sprites remain white and unchanged.
  for (const child of tileArtworkChildren(graphics)) {
    if (child instanceof Sprite) child.tint = recolor(Color.shared.setValue(child.tint).toNumber());
  }
}

/** Clear geometry and owned sprites; textures remain shared by the tile cache. */
export function redrawTileGraphic(graphics: Graphics, draw: () => void, tint?: number) {
  graphics.clear();
  clearTileArtwork(graphics);
  draw();
  graphics.tint = 0xffffff;
  if (tint !== undefined) recolorTileDetails(graphics, tint);
}
